// src/components/Dashboard/RoleDashboard/VideoProfile.jsx
import React, { useEffect, useRef, useState } from "react";
import YouTube from "react-youtube";
import { apiFetch } from "../../../apiFetch";
import { useNavigate, useLocation } from "react-router-dom";
import "./VideoProfile.css";

const ageRanges = ["13-16", "17-20", "21-24", "25-30", "31-35", "36-40", "41-50", "50+"];
const generalPositions = ["Goal Keepers", "Defenders", "Midfielders", "Forwards/Attackers"];
const specificPositions = ["GK", "LB", "RB", "CB", "CDM", "CM", "AM", "LW", "RW", "ST"];
const statuses = ["Free Agent", "Represented"];
const scoutRatingOptions = ["1", "2", "3", "4", "5"];
const genders = ["Male", "Female"];

const VideoProfile = (props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const path = (location.pathname || "").replace(/\/+$/, "");
    const isFullscreen = path.endsWith("/video-profiles");

    // ✅ derive viewer from either props OR navigation state
    const viewerId = props.viewerId ?? location.state?.viewerId ?? null;
    const viewerRole = props.viewerRole ?? location.state?.viewerRole ?? null;

    const [videos, setVideos] = useState([]);
    const [userRatings, setUserRatings] = useState({}); // ⭐ per-viewer ratings (by youtube id)
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [reportedVideos, setReportedVideos] = useState({});

    const [reportFeedback, setReportFeedback] = useState({
        videoId: null,
        text: "",
    });

    // ✅ NEW: mode (NOT a filter UI)
    // - discover => backend watchFilter=unwatched
    // - watched  => backend watchFilter=watched
    const [mode, setMode] = useState("discover"); // "discover" | "watched"

    // ✅ NEW: eliminate stale React state reads for mode
    const modeRef = useRef(mode);
    useEffect(() => {
        modeRef.current = mode;
    }, [mode]);

    const [filters, setFilters] = useState({
        gender: "",
        country: "",
        city: "",
        ageRange: "",
        generalPosition: "",
        primaryPosition: "",
        status: "",
        minRating: "",
    });

    const [countryOptions, setCountryOptions] = useState([]);
    const [citySuggestions, setCitySuggestions] = useState([]);
    const [cityLoading, setCityLoading] = useState(false);
    const [cityInput, setCityInput] = useState("");

    // ---------------------------
    // ✅ YouTube IFrame Player API refs + accurate tracking
    // ---------------------------
    const watchIntervalRef = useRef(null);
    const markSurfacedTimeoutRef = useRef(null);
    const lastClickedVideoRef = useRef(null);

    const ytPlayersRef = useRef({}); // videoId -> player
    const lastPlaybackTimeRef = useRef(0);
    const isPlayingRef = useRef(false);
    const activeVideoIdRef = useRef(null);
    const activeVideoDurationRef = useRef(300);

    const clientExpectedRef = useRef(0);

    const activeSessionIdRef = useRef(null);
    const eventCounterRef = useRef(0);

    // ✅ ensures stale /video-pool responses can't overwrite newer mode
    const videoPoolReqSeqRef = useRef(0);



    const makeClientId = () => {
        if (typeof crypto !== "undefined" && crypto.randomUUID) {
            return crypto.randomUUID();
        }

        return `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    };

    const beginWatchSession = (videoId) => {
        activeVideoIdRef.current = String(videoId);
        activeSessionIdRef.current = makeClientId();
        eventCounterRef.current = 0;
    };

    const nextEventId = () => {
        eventCounterRef.current += 1;

        const sessionId = activeSessionIdRef.current || "no-session";
        return `${sessionId}-${eventCounterRef.current}`;
    };



    const stopWatchTracking = ({ resetSession = true } = {}) => {
        if (watchIntervalRef.current) {
            clearInterval(watchIntervalRef.current);
            watchIntervalRef.current = null;
        }

        clientExpectedRef.current = 0;
        lastPlaybackTimeRef.current = 0;
        isPlayingRef.current = false;
        activeVideoIdRef.current = null;

        if (resetSession) {
            activeSessionIdRef.current = null;
            eventCounterRef.current = 0;
        }
    };

    // ✅ flush last few seconds before any UI action that could close/re-render player
    // Prevents "losing" the last <5s that haven't been sent yet.
    const flushActiveWatchProgress = async () => {
        try {
            if (!viewerId || !viewerRole) return;
            if (!isPlayingRef.current) return;

            const videoId = activeVideoIdRef.current;
            if (!videoId) return;

            const player = ytPlayersRef.current[String(videoId)];
            if (!player?.getCurrentTime) return;

            let now = 0;
            try {
                now = await player.getCurrentTime();
            } catch {
                return;
            }

            const prev = Number(lastPlaybackTimeRef.current || 0);
            let delta = now - prev;

            if (!Number.isFinite(delta) || delta <= 0) {
                lastPlaybackTimeRef.current = now;
                return;
            }

            // guard against huge jumps
            if (delta > 15) {
                lastPlaybackTimeRef.current = now;
                return;
            }

            // update baseline immediately
            lastPlaybackTimeRef.current = now;

            if (!activeSessionIdRef.current) {
                beginWatchSession(videoId);
            }

            const res = await apiFetch("/api/videos/watch-progress", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    viewerId,
                    viewerRole,
                    videoId,
                    watchTimeDelta: delta,
                    videoDuration: activeVideoDurationRef.current || 300,
                    sessionId: activeSessionIdRef.current,
                    eventId: nextEventId(),
                    clientNow: new Date().toISOString(),
                    clientExpectedWatchTimeSeconds: clientExpectedRef.current + delta,
                }),
            });

            if (!res.ok) return;

            const data = await res.json().catch(() => ({}));
            clientExpectedRef.current += delta;

            if (data?.alreadyCompleted || data?.engagementLevel === "fully_watched") {
                // safe to stop; backend is authoritative
                stopWatchTracking();
            }
        } catch {
            // ignore flush errors
        }
    };

    const startWatchTracking = (videoId, videoDuration) => {
        if (!viewerId || !viewerRole) return;

        if (watchIntervalRef.current) stopWatchTracking();

        beginWatchSession(videoId);
        activeVideoDurationRef.current = Number(videoDuration) || 300;

        // baseline from actual playback position (if ready)
        const player = ytPlayersRef.current[String(videoId)];
        try {
            const t = player?.getCurrentTime?.();
            lastPlaybackTimeRef.current = Number.isFinite(t) ? t : 0;
        } catch {
            lastPlaybackTimeRef.current = 0;
        }

        watchIntervalRef.current = setInterval(async () => {
            // only count when tab visible + actually playing
            if (document.visibilityState !== "visible") return;
            if (!isPlayingRef.current) return;

            const player = ytPlayersRef.current[String(videoId)];
            if (!player?.getCurrentTime) return;

            // ensure we’re still tracking the same video
            if (activeVideoIdRef.current !== String(videoId)) return;

            let now = 0;
            try {
                now = await player.getCurrentTime();
            } catch {
                return;
            }

            const prev = Number(lastPlaybackTimeRef.current || 0);
            let delta = now - prev;

            // no progress / seek backwards / invalid
            if (!Number.isFinite(delta) || delta <= 0) {
                lastPlaybackTimeRef.current = now;
                return;
            }

            // guard against huge jumps (sleep/tab throttle)
            if (delta > 15) {
                lastPlaybackTimeRef.current = now;
                return;
            }

            // update baseline before sending
            lastPlaybackTimeRef.current = now;

            if (!activeSessionIdRef.current) {
                beginWatchSession(videoId);
            }

            try {
                const res = await apiFetch("/api/videos/watch-progress", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        viewerId,
                        viewerRole,
                        videoId,
                        watchTimeDelta: delta,
                        videoDuration: activeVideoDurationRef.current || 300,
                        sessionId: activeSessionIdRef.current,
                        eventId: nextEventId(),
                        clientNow: new Date().toISOString(),
                        clientExpectedWatchTimeSeconds: clientExpectedRef.current + delta,
                    }),
                });

                if (!res.ok) {
                    const text = await res.text().catch(() => "");
                    console.warn("watch-progress rejected:", res.status, text);
                    return;
                }

                const data = await res.json();

                // optional drift debug
                clientExpectedRef.current += delta;
                const serverWatch = Number(data?.clampedWatchTimeSeconds);
                const diff = Math.abs(clientExpectedRef.current - serverWatch);

                if (Number.isFinite(serverWatch)) {
                    if (diff <= 2) {
                        console.log("✅ MATCH", {
                            videoId,
                            clientExpected: clientExpectedRef.current,
                            serverWatch,
                            diff,
                        });
                    } else {
                        console.warn("❌ DRIFT", {
                            videoId,
                            clientExpected: clientExpectedRef.current,
                            serverWatch,
                            diff,
                            data,
                        });
                    }
                }

                if (data?.alreadyCompleted || data?.engagementLevel === "fully_watched") {
                    stopWatchTracking();
                }
            } catch {
                // ignore network errors
            }
        }, 5000);
    };

    // --------- FETCH VIDEOS ----------
    // ✅ accepts modeOverride + latest-request-wins guard
    const fetchFilteredVideos = async (modeOverride) => {
        setLoading(true);
        setError(null);

        const seq = ++videoPoolReqSeqRef.current;

        // ✅ critical: read from ref (no stale React state)
        const effectiveMode = modeOverride ?? modeRef.current;

        try {
            const queryParams = new URLSearchParams({
                ...filters,
                viewerId: viewerId || "",
                viewerRole: viewerRole || "",
                mode: effectiveMode, // "discover" | "watched"
            }).toString();

            console.log("🎯 video-pool fetch", {
                seq,
                mode: effectiveMode,
                viewerId,
                viewerRole,
            });

            const res = await apiFetch(`/api/video-pool?${queryParams}`);

            if (!res.ok) throw new Error("Failed to load videos");

            const data = await res.json();

            // ✅ ignore stale responses
            if (seq !== videoPoolReqSeqRef.current) {
                console.warn("🧯 Ignoring stale video-pool response", {
                    seq,
                    latest: videoPoolReqSeqRef.current,
                });
                return;
            }

            const enriched = (Array.isArray(data) ? data : []).map((video) => ({
                ...video,
                showPlayer: false,
                averageRating: video.averageRating ?? video.average_rating ?? null,
                viewerRating: video.viewerRating ?? video.viewer_rating ?? null,
            }));

            // hydrate userRatings from viewerRating
            const initialRatings = {};
            enriched.forEach((v) => {
                if (v.viewerRating != null) {
                    initialRatings[String(v.youtube_video_id)] = v.viewerRating;
                }
            });

            setVideos(enriched);
            setUserRatings(initialRatings);

            // ✅ hydrate persistent reported state
            const initialReported = {};

            enriched.forEach((v) => {
                const id = String(v.youtube_video_id);

                if (v.viewerHasReported) {
                    initialReported[id] = true;
                }
            });

            setReportedVideos(initialReported);
        } catch (err) {
            console.error("Error fetching videos:", err);
            setError("Failed to load videos.");
        } finally {
            // only clear loading if this request is still the latest
            if (seq === videoPoolReqSeqRef.current) {
                setLoading(false);
            }
        }
    };

    // ✅ mode switch handler (NOT in filters) — flush first so no seconds are missed
    // ✅ no setTimeout + no race; pass nextMode directly
    const handleModeChange = async (nextMode) => {
        if (nextMode === modeRef.current) return;

        await flushActiveWatchProgress();
        stopWatchTracking();

        // update both state + ref path
        setMode(nextMode);
        modeRef.current = nextMode;

        // ✅ guaranteed correct mode + prevents discover overwrite
        fetchFilteredVideos(nextMode);
    };

    // ✅ debug mount/unmount
    useEffect(() => {
        console.log("✅ VideoProfile REALLY mounted");
        return () => {
            console.log("🧹 VideoProfile unmounted");
        };
    }, []);

    // Fetch on mount and whenever filters change
    useEffect(() => {
        if (!viewerId || !viewerRole) return;
        fetchFilteredVideos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, viewerId, viewerRole]);

    useEffect(() => {
        return () => {
            stopWatchTracking();
            if (markSurfacedTimeoutRef.current) clearTimeout(markSurfacedTimeoutRef.current);
        };
    }, []);

    useEffect(() => {
        if (cityInput.trim().length < 2) {
            setCitySuggestions([]);
            setCityLoading(false);
        }
    }, [cityInput]);


    useEffect(() => {
        const trimmed = cityInput.trim();

        const t = setTimeout(() => {
            setFilters((prev) => (
                prev.city === trimmed ? prev : { ...prev, city: trimmed }
            ));
        }, 350);

        return () => clearTimeout(t);
    }, [cityInput]);


    // Optional: stop watch tracking when filters change (flush first)
    useEffect(() => {
        (async () => {
            await flushActiveWatchProgress();
            stopWatchTracking();
            if (markSurfacedTimeoutRef.current) clearTimeout(markSurfacedTimeoutRef.current);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    // --------- FETCH COUNTRIES ----------
    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const response = await apiFetch("/countries");
                const data = await response.json();
                if (Array.isArray(data.countries)) {
                    const sorted = data.countries.sort((a, b) => a.localeCompare(b));
                    setCountryOptions(sorted);
                } else {
                    console.error("Unexpected response structure:", data);
                    setCountryOptions([]);
                }
            } catch (error) {
                console.error("Error fetching countries from custom API:", error);
                setCountryOptions([]);
            }
        };
        fetchCountries();
    }, []);

    // --------- HANDLERS ----------
    const handleCitySearch = async (value) => {
        setCityInput(value);

        const trimmed = value.trim();

        if (trimmed.length < 2) {
            setCitySuggestions([]);
            setCityLoading(false);
            return;
        }

        try {
            setCityLoading(true);
            const res = await apiFetch(`/api/filters/cities?search=${encodeURIComponent(trimmed)}`);
            if (!res.ok) throw new Error("City search failed");
            const data = await res.json();
            setCitySuggestions(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("City search failed", err);
            setCitySuggestions([]);
        } finally {
            setCityLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const togglePlayer = async (videoId, videoDurationSeconds) => {
        await flushActiveWatchProgress();
        stopWatchTracking();

        if (markSurfacedTimeoutRef.current) {
            clearTimeout(markSurfacedTimeoutRef.current);
            markSurfacedTimeoutRef.current = null;
        }

        const isSameVideo = lastClickedVideoRef.current === videoId;
        lastClickedVideoRef.current = videoId;

        if (!isSameVideo && viewerId && viewerRole) {
            markSurfacedTimeoutRef.current = setTimeout(() => {
                apiFetch("/api/videos/mark-surfaced", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ viewerId, viewerRole, videoIds: [videoId] }),
                }).catch(() => { });
            }, 2000);
        }

        setVideos((prev) =>
            prev.map((video) => {
                if (video.youtube_video_id === videoId) {
                    return { ...video, showPlayer: true, durationSeconds: videoDurationSeconds ?? video.durationSeconds };
                }
                return { ...video, showPlayer: false };
            })
        );
    };



    const showReportFeedback = (videoId, text) => {
        setReportFeedback({ videoId: String(videoId), text });

        // clear any previous timer
        if (showReportFeedback._t) clearTimeout(showReportFeedback._t);

        showReportFeedback._t = setTimeout(() => {
            setReportFeedback({ videoId: null, text: "" });
        }, 2000);
    };

    const reportVideo = async (videoId) => {
        try {
            if (!viewerId || !viewerRole) {
                showReportFeedback(videoId, "Please sign in to report.");
                return;
            }

            await apiFetch("/api/report-video", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    videoId,
                    reporterId: viewerId,
                    reporterRole: viewerRole,
                }),
            });

            setReportedVideos((prev) => ({
                ...prev,
                [videoId]: true,
            }));

            showReportFeedback(
                videoId,
                "Report received. Thank you."
            );

        } catch (err) {
            console.error("Failed to report video", err);

            showReportFeedback(
                videoId,
                "Report failed. Try again."
            );
        }
    };




    const navigateToProfile = (playerSlug) => {
        if (!playerSlug) return;

        const role = (viewerRole || "agent").toLowerCase();

        navigate(`/${role}-dashboard/player-profile/${playerSlug}`, {
            state: { viewerRole: role, viewerId: viewerId || null, from: "role-video-profile" },
        });
    };

    const handleRate = async (videoId, ratingValue) => {
        if (!viewerId || viewerRole === "player") return;

        const normalizedId = String(videoId);

        setUserRatings((prev) => ({ ...prev, [normalizedId]: ratingValue }));

        try {
            const res = await apiFetch("/api/rate-video", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ videoId, viewerId, viewerRole, rating: ratingValue }),
            });

            if (!res.ok) throw new Error("Rating failed");
        } catch (err) {
            console.error("❌ Failed to submit rating:", err);
        }
    };

    const renderRating = (videoId) => {
        const canRate = !!viewerId && viewerRole !== "player";
        const key = String(videoId);
        const effectiveRating = userRatings[key] ?? 0;

        return (
            <div className="rating-stars">
                {[1, 2, 3, 4, 5].map((value) => (
                    <span
                        key={value}
                        onClick={() => canRate && handleRate(videoId, value)}
                        style={{
                            color: value <= effectiveRating ? "#FFD700" : "#888",
                            cursor: canRate ? "pointer" : "default",
                            fontSize: "20px",
                        }}
                    >
                        ★
                    </span>
                ))}
            </div>
        );
    };

    // --------- RENDER ----------
    return (
        <div className={`role-video-profile ${isFullscreen ? "is-fullscreen" : "is-embedded"}`}>
            <div className="video-profile-wrapper">
                <div className="video-profile-header">

                    {/* TITLE ROW */}
                    <div className="header-top-row">
                        <h2 className="video-profile-title">Player Video Profile</h2>

                        {/* ⛶ Expand — ONLY EMBEDDED (same row as title) */}
                        {!isFullscreen && (
                            <button
                                type="button"
                                className="fullscreen-btn header-expand-btn"
                                title="Open fullscreen"
                                onClick={() => {
                                    const role = (viewerRole || "agent").toLowerCase();
                                    navigate(`/${role}-dashboard/video-profiles`, {
                                        state: { viewerId, viewerRole: role },
                                    });
                                }}
                            >
                                ⛶
                            </button>
                        )}
                    </div>

                    {/* ACTION ROW */}
                    <div className="header-actions">
                        {/* MODE TOGGLE — ONLY FULLSCREEN */}
                        {isFullscreen && (
                            <div
                                className="video-profile-mode-toggle"
                                role="tablist"
                                aria-label="Video profile mode"
                            >
                                <button
                                    type="button"
                                    className={`mode-pill ${mode === "discover" ? "active" : ""}`}
                                    onClick={() => handleModeChange("discover")}
                                    aria-pressed={mode === "discover"}
                                    title="Discover new (unwatched) videos"
                                >
                                    Discover
                                </button>

                                <button
                                    type="button"
                                    className={`mode-pill ${mode === "watched" ? "active" : ""}`}
                                    onClick={() => handleModeChange("watched")}
                                    aria-pressed={mode === "watched"}
                                    title="Review watched (90%+) videos"
                                >
                                    Watched
                                </button>
                            </div>
                        )}


                        {/* RIGHT SIDE BUTTONS */}
                        <div className="header-buttons">

                            {/* Refresh — ONLY FULLSCREEN */}
                            {isFullscreen && (
                                <button
                                    type="button"
                                    className="video-profile-refresh-btn"
                                    title="Refresh videos"
                                    onClick={async () => {
                                        await flushActiveWatchProgress?.();
                                        stopWatchTracking();
                                        fetchFilteredVideos();
                                    }}
                                >
                                    Refresh
                                </button>
                            )}

                        </div>
                    </div>
                </div>



                <div className="filters">
                    <select name="gender" onChange={handleChange} value={filters.gender}>
                        <option value="">All Genders</option>
                        {genders.map((g) => (
                            <option key={g} value={g}>
                                {g}
                            </option>
                        ))}
                    </select>

                    <select name="country" onChange={handleChange} value={filters.country}>
                        <option value="">All Countries</option>
                        {countryOptions.map((country) => (
                            <option key={country} value={country}>
                                {country}
                            </option>
                        ))}
                    </select>

                    <div className="city-filter-wrapper">
                        <input
                            type="text"
                            placeholder="City"
                            value={cityInput}
                            onChange={(e) => handleCitySearch(e.target.value)}
                        />

                        {cityInput.trim().length >= 2 && citySuggestions.length > 0 && (
                            <ul className="city-suggestions">
                                {citySuggestions.map((city) => (
                                    <li
                                        key={city}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            setCitySuggestions([]);
                                            setCityLoading(false);
                                            const selectedCity = city.trim();
                                            setCityInput(selectedCity);
                                            setFilters((prev) => ({ ...prev, city: selectedCity }));
                                        }}
                                    >
                                        {city}
                                    </li>
                                ))}
                            </ul>
                        )}

                        {cityLoading && cityInput.trim().length >= 2 && (
                            <div className="city-loading">Searching…</div>
                        )}
                    </div>

                    <select name="ageRange" onChange={handleChange} value={filters.ageRange}>
                        <option value="">All Age Ranges</option>
                        {ageRanges.map((range) => (
                            <option key={range} value={range}>
                                {range}
                            </option>
                        ))}
                    </select>

                    <select name="generalPosition" onChange={handleChange} value={filters.generalPosition}>
                        <option value="">All General Positions</option>
                        {generalPositions.map((pos) => (
                            <option key={pos} value={pos}>
                                {pos}
                            </option>
                        ))}
                    </select>

                    <select name="primaryPosition" onChange={handleChange} value={filters.primaryPosition}>
                        <option value="">All Specific Positions</option>
                        {specificPositions.map((pos) => (
                            <option key={pos} value={pos}>
                                {pos}
                            </option>
                        ))}
                    </select>

                    <select name="status" onChange={handleChange} value={filters.status}>
                        <option value="">All Statuses</option>
                        {statuses.map((status) => (
                            <option key={status} value={status}>
                                {status}
                            </option>
                        ))}
                    </select>

                    <select
                        name="minRating"
                        onChange={handleChange}
                        value={filters.minRating}
                    >
                        <option value="">All Scout Ratings</option>

                        {scoutRatingOptions.map((r) => (
                            <option key={r} value={r}>
                                {r} Star{r !== "1" ? "s" : ""}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="video-profile-scroll-container fade-in">
                    {loading && videos.length === 0 && <p>Loading player videos...</p>}
                    {loading && videos.length > 0 && <p>Updating videos…</p>}
                    {error && <p className="error-message">{error}</p>}

                    {!loading && !error && videos.length === 0 && (
                        <p className="no-videos-message" style={{ color: "#000" }}>
                            No videos match the selected criteria.
                        </p>
                    )}

                    <div className="video-profile-grid">
                        {videos.map((video) => {
                            const playerSlug =
                                video.player?.profileSlug || video.player?.slug || video.playerProfileSlug || null;

                            return (
                                <div key={video.youtube_video_id} className="video-item-wrapper">
                                    <div className="video-card">

                                    {/* ✅ Report feedback shown inside this card */}
                                    {reportFeedback.videoId === String(video.youtube_video_id) && (
                                        <div className="report-feedback">
                                            {reportFeedback.text}
                                        </div>
                                    )}

                                    {!video.showPlayer ? (
                                        <div
                                            className="thumbnail-container"
                                            onClick={() => togglePlayer(video.youtube_video_id, video.durationSeconds)}
                                        >

                                            <img src={video.thumbnail_url} alt={video.title} />
                                            <div className="play-button">▶</div>
                                        </div>
                                    ) : (
                                        <div className="video-wrapper">

                                            <YouTube
                                                videoId={video.youtube_video_id}
                                                iframeClassName="youtube-iframe"
                                                opts={{
                                                    width: "100%",
                                                    height: "100%",
                                                    playerVars: {
                                                        autoplay: 1,
                                                        modestbranding: 1,
                                                        rel: 0,
                                                        controls: 1,
                                                    },
                                                }}

                                                iframeProps={{
                                                    allowFullScreen: true,
                                                }}

                                                onReady={(e) => {
                                                    ytPlayersRef.current[String(video.youtube_video_id)] = e.target;

                                                    try {
                                                        lastPlaybackTimeRef.current = e.target.getCurrentTime() || 0;
                                                    } catch {
                                                        lastPlaybackTimeRef.current = 0;
                                                    }
                                                }}
                                                onStateChange={(e) => {
                                                    const isActive = videos.some(
                                                        (v) =>
                                                            v.showPlayer &&
                                                            String(v.youtube_video_id) === String(video.youtube_video_id)
                                                    );

                                                    if (!isActive) {
                                                        isPlayingRef.current = false;
                                                        return;
                                                    }

                                                    if (e.data === 1) {
                                                        isPlayingRef.current = true;

                                                        try {
                                                            lastPlaybackTimeRef.current =
                                                                e.target.getCurrentTime() || lastPlaybackTimeRef.current || 0;
                                                        } catch { }

                                                        if (!watchIntervalRef.current) {
                                                            startWatchTracking(
                                                                video.youtube_video_id,
                                                                video.durationSeconds || video.duration_seconds || 300
                                                            );
                                                        }

                                                    } else if (e.data === 2) {
                                                        // ⏸ PAUSED → flush progress
                                                        (async () => {
                                                            await flushActiveWatchProgress();
                                                            isPlayingRef.current = false;
                                                        })();

                                                    } else if (e.data === 0) {
                                                        // ⏹ ENDED
                                                        (async () => {
                                                            await flushActiveWatchProgress();
                                                            stopWatchTracking();
                                                        })();

                                                    } else {
                                                        isPlayingRef.current = false;
                                                    }
                                                }}
                                            />
                                        </div>
                                    )}

                                        <div className="video-content">
                                            <h4>{video.title}</h4>
                                        </div>

                                    </div>

                                    <div className="video-actions-bar">

                                        <button
                                            className="vsn-profile-btn"
                                            onClick={() => navigateToProfile(playerSlug)}
                                            title="Click to open Player Profile"
                                        >
                                            VSN
                                        </button>

                                        <div className="video-scout-rating">
                                            <span className="rating-label">Scout Rating</span>
                                            {renderRating(video.youtube_video_id)}
                                        </div>

                                        <button
                                            className="report-video-btn"
                                            onClick={() => reportVideo(video.youtube_video_id)}
                                            disabled={reportedVideos[video.youtube_video_id]}
                                        >
                                            {reportedVideos[video.youtube_video_id]
                                                ? "Reported"
                                                : "Report"}
                                        </button>

                                    </div>

                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VideoProfile;






